import { Component } from "@angular/core";
import { GetRowIdParams, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import _ from "lodash";
import { AgFns, GridOptionsExt } from "@/shared/ag-fns";
import { ButtonRendererComponent } from "@/shared/button-renderer.component";
import { DomainBaseComponent } from "@/shared/domain-base.component";
import { DomainService } from "@/shared/domain.service";
import { StagedFeedApiService } from "./staged-feed-api.service";
import { BulkRepointColorDialogComponent } from "./bulk-repoint-color-dialog.component";
import { SizeMappingDialogComponent } from "@/shared/size-mapping-dialog.component";
import { BulkRetireDialogComponent } from "./bulk-retire-dialog.component";
import { AnalysisGridDialogComponent } from "@/shared/analysis-grid-dialog.component";
import { PANTONE_HEX } from "./pantone-lookup";
import { DialogBaseComponent } from "@/shared/dialog-base.component";
import { RepointColorDialogComponent } from "@/shared/repoint-color-dialog.component";
import { ActiveStatusEnum, FeatureChoice } from "@/model/entities/entity-model";
import {
  RetireStyleDTO,
  StagedFeedManufacturer,
  StagedFeedRunDTO,
  StagedProductColorDTO,
  StagedProductTypeDTO,
  StagedStyleDetailDTO,
  UnmatchedUniformaxColorDTO,
} from "./staged-feed.models";

/**
 * Read-only view of what a manufacturer publishes, next to what Uniformax carries.
 *
 * Phase 0 of the SanMar API switch. Nothing here changes Uniformax data — it exists so the
 * divergence can be seen before anyone decides what to do about it.
 */
@Component({
  selector: "app-staged-feed",
  templateUrl: "./staged-feed.component.html",
  standalone: false,
})
export class StagedFeedComponent extends DomainBaseComponent {
  styleGridOptions: GridOptionsExt;
  colorGridOptions: GridOptionsExt;
  unmatchedGridOptions: GridOptionsExt;

  manufacturers: StagedFeedManufacturer[] = [];
  manufacturerId: number | null = null;

  styles: StagedProductTypeDTO[] = [];
  filteredStyles: StagedProductTypeDTO[] = [];
  /** Default on: a feed in Setup is a worklist, not a catalogue browser. */
  showOnlyNeedingAttention = true;

  /**
   * What stands between each style and retirement, by ProductTypeId.
   *
   * Read alongside the styles so the grid can say WHY a style cannot be retired - open job
   * orders, open purchase order quantity, stock on a shelf - rather than offering a button that
   * leads to a screen that then refuses. The retirement screen computes the same verdicts and
   * remains the authority; this is the grid being honest in advance.
   */
  private retireByProductTypeId = new Map<number, RetireStyleDTO>();
  latestRun: StagedFeedRunDTO | null = null;
  detail: StagedStyleDetailDTO | null = null;

  isLoading = false;
  isStaging = false;
  singleStyle = "";
  maxStyles: number | null = 10;
  statusMessage = "";

  /**
   * A sweep is one HTTP call per style and 478 styles take the best part of an hour, so the page
   * polls rather than leaving an operator pressing Refresh to find out whether it is still alive.
   * Only the run row is re-read on each tick - re-reading 649 styles every few seconds would cost
   * more than the sweep itself. The grid is refreshed once, when the sweep finishes.
   */
  private static readonly POLL_MS = 4000;
  private pollHandle: any = null;
  isSweepRunning = false;

  /**
   * One dialog at a time.
   *
   * The dialogs open with focusOnShow false, so focus stays on the toolbar button that opened
   * them. Pressing Enter re-fires that button and opens a second dialog over the first, which
   * fails with 'Cannot read properties of null'. The guard is set before the await, not inside
   * the dialog, because by the time the dialog exists the second open has already started.
   */
  private isDialogOpen = false;

  constructor(protected domainService: DomainService, private api: StagedFeedApiService) {
    super(domainService);

    this.styleGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onStyleGridReady,
      onRowSelected: this.onStyleRowSelected,
      rowSelection: "single",
      getRowId: (p: GetRowIdParams) => String((p.data as StagedProductTypeDTO).Id),
      getRowClass: (p: any) => ((p.data as StagedProductTypeDTO)?.FetchError ? "rs-already-processed" : ""),
    });

    this.colorGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onColorGridReady,
      getRowId: (p: GetRowIdParams) => String((p.data as StagedProductColorDTO).Id),
      cellSelection: false,
    });

    this.unmatchedGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onUnmatchedGridReady,
      getRowId: (p: GetRowIdParams) => String((p.data as UnmatchedUniformaxColorDTO).FeatureChoiceId),
      cellSelection: false,
    });

    this.prepare();
  }

  private async prepare() {
    await this.loadManufacturers();
    this.isPageReady = true;
    if (this.manufacturerId != null) await this.refresh();
  }

  /** Loads the list and selects the first manufacturer with feed styles. */
  private async loadManufacturers() {
    this.manufacturers = (await this.api.getManufacturers())
      .map(m => ({ ...m, Label: m.IsFeedOff ? `${m.Name} (feed is off)` : m.Name }));
    const first = this.manufacturers.find(m => m.StagedStyleCount > 0 && !m.IsFeedOff)
      ?? this.manufacturers[0];
    this.manufacturerId = first?.Id ?? null;
  }

  get isFeedOff(): boolean {
    return this.selectedManufacturer?.IsFeedOff ?? false;
  }

  get selectedManufacturer(): StagedFeedManufacturer | undefined {
    return this.manufacturers.find(m => m.Id === this.manufacturerId);
  }

  get selectedManufacturerName(): string {
    return this.selectedManufacturer?.Name ?? "";
  }

  /**
   * Readiness is per manufacturer, because it is per feed. Asking the server once for the whole
   * page would answer for whichever feed it happened to pick.
   */
  get isConfigured(): boolean {
    return this.selectedManufacturer?.IsConfigured ?? true;
  }

  /**
   * Whether the color panels belong on screen at all.
   *
   * A one-color-per-style supplier has no color question: the style IS the color, so the two
   * grids below show an empty box and a heading about something the manufacturer does not vary.
   * Collapsing them gives the whole page to the styles, which is the worklist.
   */
  get showColorPanels(): boolean {
    return this.selectedManufacturer?.RequiresColor ?? true;
  }

  /** The server names the keys to set, so this template never hardcodes one feed's. */
  get configurationHint(): string {
    return this.selectedManufacturer?.ConfigurationHint ?? "";
  }

  isRemoving = false;

  /**
   * Remove Feed Styles is off. One press deletes a manufacturer's whole alignment - 198 hand
   * answers on Red Kap - and ProductAdmin is too broad a role to guard that. It comes back when
   * the top authorization level exists. The server refuses it too (RemoveFeedStyles).
   */
  readonly canRemoveFeedStyles = false;

  /**
   * Delete the manufacturer's feed styles and set its feed status to None, in one step.
   *
   * The confirm names the hand answers, because those are the only part that cannot be read
   * again: a size or color matched by hand is stored on the feed rows and goes with them.
   */
  async onRemoveFeedStyles() {
    const m = this.selectedManufacturer;
    if (!this.canRemoveFeedStyles || m == null || this.isRemoving) return;

    const summary = await this.api.getFeedStyleSummary(m.Id);
    const handWork = summary.SizesMatchedByHand + summary.ColorsMatchedByHand > 0
      ? `<br/><br/><b>This also deletes work done by hand: ${summary.SizesMatchedByHand} feed size(s)`
        + ` and ${summary.ColorsMatchedByHand} feed color(s) that a person matched.`
        + ` If you read the feed again, you must match them again.</b>`
      : "";

    const ok = await this.dialogService.yesNoDialog(
      "Remove Feed Styles",
      `The system deletes the ${summary.StyleCount} feed style(s) and ${summary.PartCount} feed part(s)`
        + ` read for ${m.Name}. Then it sets the feed status of ${m.Name} to None.`
        + handWork
        + `<br/><br/>This does not change any product type or product. The record of each feed read stays.`
        + `<br/><br/>Do you want to continue?`,
      "Ok",
      "Cancel"
    );
    if (!ok) return;

    this.isRemoving = true;
    try {
      const r = await this.api.removeFeedStyles(m.Id);
      this.toastr.success(
        `The system deleted ${r.StylesRemoved} feed style(s) for ${m.Name} and set its feed status to None.`);

      // The manufacturer is off the list now, so the page starts again on whichever comes first.
      this.detail = null;
      this.styles = [];
      this.latestRun = null;
      await this.loadManufacturers();
      this.applyStyleFilter();
      if (this.manufacturerId != null) await this.refresh();
    } finally {
      this.isRemoving = false;
    }
  }

  async onManufacturerChange() {
    this.detail = null;
    await this.refresh();
  }

  async refresh() {
    if (this.manufacturerId == null) return;
    this.isLoading = true;
    try {
      const [styles, runs] = await Promise.all([
        this.api.getStyles(this.manufacturerId),
        this.api.getRuns(this.manufacturerId),
      ]);
      this.styles = styles;
      await this.loadRetireVerdicts();
      this.latestRun = runs.length > 0 ? runs[0] : null;
      this.applyStyleFilter();
      // Picks up a sweep someone else started, or one still running from before a page reload.
      if (this.latestRun != null && !this.latestRun.IsComplete) this.beginPoll();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Why each style cannot be retired, if it cannot.
   *
   * Read so the Error column can say WHY a style cannot be retired - open job orders, unarrived
   * purchase order quantity, stock on a shelf - beside the refusal that sent somebody looking,
   * rather than leaving them to find out inside Retire Unavailable Products.
   *
   * Failure is not fatal. Without it the Error column loses the note; that dialog does the same
   * check again and remains the authority.
   */
  private async loadRetireVerdicts() {
    this.retireByProductTypeId = new Map();
    if (this.manufacturerId == null) return;
    try {
      const scope = await this.api.getRetirementCandidates(this.manufacturerId);
      for (const st of scope.Styles) this.retireByProductTypeId.set(st.ProductTypeId, st);
    } catch {
      // Leave the map empty. See the remarks above.
    }
  }

  /** What is holding this style in the catalogue, in the operator's words. Null when nothing is. */
  private retireBlocker(row: StagedProductTypeDTO): string | null {
    const v = row?.ProductTypeId == null ? null : this.retireByProductTypeId.get(row.ProductTypeId);
    if (v == null || v.Verdict === "Retire") return null;

    // Not a retirement at all. The feed has not said the manufacturer stopped making the style.
    if (v.Verdict === "FixStyleCode") return "You cannot retire this style. Correct the SKU first.";
    if (v.Verdict === "ReadAgain") return "You cannot retire this style. Read the style again first.";

    // Most serious first - a promise already made outranks stock on a shelf.
    if (v.OpenJoDetailCount > 0) {
      return `You cannot retire this style. It is on ${v.OpenJoDetailCount} open job order line(s).`;
    }
    if (v.OpenPoQty > 0) {
      return `You cannot retire this style. ${v.OpenPoQty} unit(s) are still to arrive on a purchase order.`;
    }
    if (v.InInventoryQty > 0) {
      return `You cannot retire this style. ${v.InInventoryQty} unit(s) are still in inventory.`;
    }
    return "You cannot retire this style yet.";
  }

  // ------------------------------------------------------------------
  // Sweep progress
  // ------------------------------------------------------------------

  get sweepDone() {
    const r = this.latestRun;
    return r == null ? 0 : (r.StylesSucceeded ?? 0) + (r.StylesFailed ?? 0);
  }

  get sweepTotal() {
    return this.latestRun?.StylesRequested ?? 0;
  }

  get sweepPercent() {
    return this.sweepTotal > 0 ? Math.floor((this.sweepDone / this.sweepTotal) * 100) : 0;
  }

  /**
   * Clock-derived values are FIELDS, never getters.
   *
   * Angular evaluates a template getter twice per change-detection cycle in dev mode and compares
   * the results. Anything reading Date.now() can tick between the two reads and throws
   * NG0100 - '37s' on the first pass, '38s' on the second. Recomputing on a timer and binding to
   * a settled field avoids that entirely.
   */
  sweepElapsed = "";
  sweepRemaining = "";
  sweepRate = "";

  private tickHandle: any = null;

  private updateSweepTimings() {
    const r = this.latestRun;
    if (r == null) {
      this.sweepElapsed = this.sweepRemaining = this.sweepRate = "";
      return;
    }

    const end = r.CompletedTs ? new Date(r.CompletedTs).getTime() : Date.now();
    const elapsedMs = Math.max(0, end - new Date(r.StartedTs).getTime());
    const done = this.sweepDone;

    this.sweepElapsed = this.formatDuration(elapsedMs);

    // Flat average over the run so far. Good enough to answer "do I wait or come back later".
    this.sweepRemaining = done > 0 && this.sweepTotal > 0 && this.isSweepRunning
      ? this.formatDuration((elapsedMs / done) * (this.sweepTotal - done))
      : "";

    this.sweepRate = done > 0 && elapsedMs > 0
      ? (done / (elapsedMs / 60000)).toFixed(1)
      : "";
  }

  private formatDuration(ms: number) {
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }

  private beginPoll() {
    if (this.pollHandle != null) return;
    this.isSweepRunning = true;
    this.pollHandle = setInterval(() => this.pollRun(), StagedFeedComponent.POLL_MS);
    // The counts move on the 4s poll; the clock moves every second, so it ticks separately.
    this.tickHandle = setInterval(() => this.updateSweepTimings(), 1000);
    this.updateSweepTimings();
  }

  private stopPoll() {
    if (this.pollHandle != null) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
    if (this.tickHandle != null) {
      clearInterval(this.tickHandle);
      this.tickHandle = null;
    }
    this.isSweepRunning = false;
    // One last pass so the finished panel shows the real total rather than the last tick.
    this.updateSweepTimings();
  }

  private async pollRun() {
    if (this.manufacturerId == null) return;
    try {
      const runs = await this.api.getRuns(this.manufacturerId);
      this.latestRun = runs.length > 0 ? runs[0] : null;
      if (this.latestRun == null || this.latestRun.IsComplete) {
        this.stopPoll();
        // One full reload at the end, so the grid reflects everything the sweep staged.
        await this.refresh();
        this.statusMessage = this.latestRun == null
          ? ""
          : `The sweep finished. The system read ${this.latestRun.StylesSucceeded} style(s)`
            + ` in ${this.sweepElapsed}`
            + (this.latestRun.StylesFailed > 0 ? `, and ${this.latestRun.StylesFailed} failed.` : ".");
      }
    } catch {
      // A failed poll is not worth interrupting anyone over - the next tick tries again, and
      // the sweep is running on the server either way.
    }
  }

  override ngOnDestroy() {
    this.stopPoll();
    super.ngOnDestroy();
  }

  /**
   * The styles that need somebody, rather than the ones with an unmatched COLOR.
   *
   * This filter used to keep only styles carrying a color the feed does not publish, which hid
   * two things it should never have hidden. A style the manufacturer REFUSED has no staged
   * colors at all, so it scored zero and vanished - on SanMar that quietly hid 35 of 649. And a
   * manufacturer with no color feature scores zero on every style, so Red Kap's entire
   * catalogue disappeared behind a filter that is on by default: 332 styles, 118 of them
   * failures, and an empty grid saying the sweep had found nothing.
   *
   * A style wants attention if the manufacturer would not answer for it, or if we sell a color
   * it does not publish. Those are different problems and both belong in the same queue.
   */
  applyStyleFilter() {
    // The server decides, from the classification Edit Product Type shows. Rebuilding it here from
    // three counts hid every style whose only problem was a feature, a second product type or a
    // product the feed does not make. Retired and never-carried styles are never work.
    this.filteredStyles = this.showOnlyNeedingAttention
      ? this.styles.filter(s => s.NeedsAttention)
      : this.styles;
    this.styleGridOptions.apiX?.setGridOption("rowData", this.filteredStyles);
  }

  /** Pull one style straight from the manufacturer. The prove-it-works button. */
  async stageOneStyle() {
    if (this.manufacturerId == null || !this.singleStyle.trim()) return;
    this.isStaging = true;
    this.statusMessage = "";
    try {
      const run = await this.api.stageStyle(this.manufacturerId, this.singleStyle.trim());
      this.statusMessage =
        run == null
          ? "The system did not return a result."
          : run.StylesFailed > 0
            ? `The style did not load. ${run.Error ?? ""}`
            : `The system staged ${run.ColorsStaged} colors and ${run.PartsStaged} parts.`;
      await this.refresh();
      const row = this.styles.find(s => s.Style === this.singleStyle.trim());
      if (row) await this.loadDetail(row.Id);
    } catch (e: any) {
      this.statusMessage = `The style did not load. ${e?.error?.Message ?? e?.message ?? ""}`;
    } finally {
      this.isStaging = false;
    }
  }

  /** Queue the full sweep. One call per style, so this runs in the background. */
  async startSweep() {
    if (this.manufacturerId == null) return;
    const count = this.maxStyles == null ? "every style" : `the first ${this.maxStyles} styles`;
    const ok = await this.dialogService.yesNoDialog(
      "Start the sweep",
      `The system reads ${count} for ${this.selectedManufacturerName} from the manufacturer.`
        + " This runs in the background. Do you want to continue?",
      "Ok",
      "Cancel"
    );
    if (!ok) return;

    this.isStaging = true;
    try {
      await this.api.startSweep(this.manufacturerId, this.maxStyles);
      this.statusMessage = "";
      // Hangfire picks the job up a moment later, so the first poll may still show the previous
      // run. That is harmless - the panel corrects itself on the next tick.
      await this.pollRun();
      this.beginPoll();
    } catch (e: any) {
      this.statusMessage = `The sweep did not start. ${e?.error?.Message ?? e?.message ?? ""}`;
    } finally {
      this.isStaging = false;
    }
  }

  async onStyleRowSelected(e: RowSelectedEvent) {
    if (!e.node.isSelected()) return;
    const row = e.data as StagedProductTypeDTO;
    // Fill the Style box so Read One Style re-reads what is on screen without any retyping.
    this.singleStyle = row.Style;
    await this.loadDetail(row.Id);
  }

  private async loadDetail(stagedProductTypeId: number) {
    this.detail = await this.api.getStyleDetail(stagedProductTypeId);
    this.colorGridOptions.apiX?.setGridOption("rowData", this.detail?.StagedColors ?? []);
    this.unmatchedGridOptions.apiX?.setGridOption("rowData", this.detail?.UnmatchedUniformaxColors ?? []);
  }

  /** Move every product of this style off a color the manufacturer does not publish. */
  async repointColor(row: UnmatchedUniformaxColorDTO) {
    const productTypeIds = this.detail?.UniformaxProductTypeIds ?? [];
    if (productTypeIds.length === 0) {
      await this.dialogService.showOkMessage(
        "No Product Type",
        "This style is not in Uniformax, so there is nothing to change.");
      return;
    }
    const result = await DialogBaseComponent.open(RepointColorDialogComponent, this, {
      productTypeIds,
      styleCode: this.detail.Style.Style,
      fromFeatureChoiceId: row.FeatureChoiceId,
      fromColorName: row.UniformaxColorName,
      stagedColors: this.detail.StagedColors,
    });
    if (result) {
      this.toastr.success(`The system changed ${result.changed} product(s) to '${result.toName}'.`);
      await this.reloadDetail();
    }
  }

  /**
   * Move one Uniformax color to another across every style of this manufacturer.
   *
   * The same operation as the per-style Change button, run over the whole manufacturer. It is
   * on the toolbar rather than the color grid because the color being retired is usually not
   * the one in front of you - it is on a hundred other styles too.
   */
  async openBulkRepoint() {
    if (this.manufacturerId == null || this.isDialogOpen) return;
    this.isDialogOpen = true;
    try {
      await this.openBulkRepointCore();
    } finally {
      this.isDialogOpen = false;
    }
  }

  private async openBulkRepointCore() {
    const result = await DialogBaseComponent.open(BulkRepointColorDialogComponent, this, {
      manufacturerId: this.manufacturerId,
      manufacturerName: this.selectedManufacturerName,
    });
    if (result) {
      this.toastr.success(
        `The system changed the color of ${result.repointedProducts} product(s)`
          + ` and combined ${result.combinedProducts} product(s)`
          + ` on ${result.changedStyles} style(s) to '${result.toName}'.`);
      await this.refresh();
      if (this.detail) await this.reloadDetail();
    }
  }

  /**
   * Every style the manufacturer rejected, in the reusable Analyze grid.
   *
   * Plain objects, not the DTOs straight from the grid: the Analyze dialog offers pivot and row
   * grouping, and those re-key rows as plain data.
   */
  async openErrorAnalysis() {
    if (this.isDialogOpen) return;

    const rows = this.styles
      .filter(s => !!s.FetchError)
      .map(s => ({
        Style: s.Style,
        // Ours, not the feed's: SanMar rejected these styles, so it never sent a name or a brand.
        Description: s.UniformaxDescription ?? s.ProductName ?? "",
        InUniformax: s.ProductTypeId == null ? "No" : s.IsCustomStyle ? "Custom style" : "Yes",
        UniformaxColors: s.UniformaxColorCount,
        NotInFeed: s.UnmatchedUniformaxColorCount,
        SizesToAnswer: s.UnansweredSizeCount,
        LastRead: s.SnapshotTs ? new Date(s.SnapshotTs) : null,
        Error: s.FetchError,
      }));

    if (rows.length === 0) {
      await this.dialogService.showOkMessage(
        "No Errors",
        "The manufacturer answered for every style. There is nothing to export.");
      return;
    }

    this.isDialogOpen = true;
    try {
      await DialogBaseComponent.open(AnalysisGridDialogComponent, this, {
        title: `Feed Errors — ${this.selectedManufacturerName}`,
        subtitle: `${rows.length} style(s) the manufacturer did not return.`
          + " Most are styles it no longer makes; those should be retired in Uniformax.",
        exportFileName: "feed-errors",
        rowData: rows,
        columnDefs: [
          { field: "Style", headerName: "Style", ...AgFns.flexWidth(140), filter: "agTextColumnFilter" },
          { field: "Description", headerName: "Our Description", ...AgFns.flexWidth(340), filter: "agTextColumnFilter" },
          { field: "InUniformax", headerName: "In Uniformax", ...AgFns.fixWidth(130), filter: "agSetColumnFilter" },
          { field: "UniformaxColors", headerName: "Our Colors", ...AgFns.fixWidth(120), type: "numericColumn" },
          { field: "NotInFeed", headerName: "Not In Feed", ...AgFns.fixWidth(120), type: "numericColumn" },
          { field: "SizesToAnswer", headerName: "Sizes to Answer", ...AgFns.fixWidth(140), type: "numericColumn" },
          { field: "LastRead", headerName: "Last Read", ...AgFns.flexWidth(160), filter: "agDateColumnFilter" },
          { field: "Error", headerName: "What the Manufacturer Said", ...AgFns.flexWidth(420), filter: "agTextColumnFilter" },
        ],
      });
    } finally {
      this.isDialogOpen = false;
    }
  }

  /**
   * Retire the feed styles and products the feed does not answer for — the catalogue carries
   * things the manufacturer no longer makes, and no mapping can fix that.
   */
  async openBulkRetire(initialTab: "styles" | "products" = "styles") {
    if (this.manufacturerId == null || this.isDialogOpen) return;
    this.isDialogOpen = true;
    try {
      const result = await DialogBaseComponent.open(BulkRetireDialogComponent, this, {
        manufacturerId: this.manufacturerId,
        manufacturerName: this.selectedManufacturerName,
        initialTab,
      });
      if (result) {
        this.toastr.success(
          `The system put ${result.retiredStyles} style(s) and ${result.retiredProducts} product(s) on Phaseout.`);
        await this.refresh();
      }
    } finally {
      this.isDialogOpen = false;
    }
  }

  /**
   * Say what each size the manufacturer publishes means in Uniformax.
   *
   * Sizes are mapped rather than renamed. Nothing downstream joins on a size string, and the
   * eleven size lists exist for real reasons, so bending them to one supplier would cost more
   * than it saved.
   *
   * Opens narrowed to the selected style's size list. The queue runs all eleven scales together,
   * so selecting a trouser style and pressing this opened on a hundred alpha-size questions that
   * belonged to other styles. The banner says what it narrowed to and Show all drops it.
   */
  async openSizeMapping() {
    if (this.manufacturerId == null || this.isDialogOpen) return;
    this.isDialogOpen = true;
    try {
      const result = await DialogBaseComponent.open(SizeMappingDialogComponent, this, {
        manufacturerId: this.manufacturerId,
        manufacturerName: this.selectedManufacturerName,
        // Null when no style is selected, or when the selected one has no size scale attached.
        // Both leave the whole queue standing, which is what the button did before.
        focusFeatureId: this.detail?.SizeFeatureId ?? undefined,
        focusFeatureName: this.detail?.SizeFeatureName ?? undefined,
        focusLabel: this.detail?.Style?.Style,
      });
      if (result) await this.refresh();
    } finally {
      this.isDialogOpen = false;
    }
  }

  /**
   * Add a color the manufacturer publishes to the Uniformax color list.
   *
   * This creates the FeatureChoice only. The style does not carry the color until someone
   * adds the products, so the confirm text says so - otherwise the filled-in cell reads as
   * "we sell this now", which is not what happened.
   */
  async createColor(row: StagedProductColorDTO) {
    const name = (row.StandardColorName || row.ColorName || "").trim();
    if (!name) {
      await this.dialogService.showOkMessage(
        "No Color Name",
        "The manufacturer did not publish a name for this color. You cannot create it.");
      return;
    }
    if (name.length > 50) {
      await this.dialogService.showOkMessage(
        "The Name Is Too Long",
        `The color name '${name}' is more than 50 characters.`
          + " Add the color in Setup and give it a shorter name.");
      return;
    }

    // Check the live color list, not the snapshot. Another operator can add the color
    // between the feed read and this click, and a second row with the same name is a
    // duplicate the name matcher cannot resolve.
    const colorFeatureId = await this.dbQueryService.getColorFeatureId();
    if (colorFeatureId == null) {
      await this.dialogService.showOkMessage('No Color Feature',
        'No feature is set up as a color feature. Set one in Setup, then try again.');
      return;
    }
    const choices = await this.dbQueryService.getFeatureChoices(colorFeatureId);
    const existing = choices.find(fc => (fc.choiceValue ?? "").trim().toLowerCase() === name.toLowerCase());
    if (existing) {
      await this.dialogService.showOkMessage(
        "The Color Is Already There",
        `The Uniformax color '${existing.choiceValue}' is already in the list.`
          + " The system refreshes this style.");
      await this.reloadDetail();
      return;
    }

    const ok = await this.dialogService.yesNoDialog(
      "Create Color",
      `The system adds '${name}' to the Uniformax color list.`
        + "<br/><br/>This creates the color only. To sell this color, you must also add the products."
        + "<br/><br/>Do you want to continue?",
      "Ok",
      "Cancel"
    );
    if (!ok) return;

    try {
      this.dbSaveService.createEntity(FeatureChoice, {
        featureId: colorFeatureId,
        choiceValue: name,
        activeStatusId: ActiveStatusEnum.Active,
        displayOrder: (_.max(choices.map(fc => fc.displayOrder ?? 0)) ?? 0) + 1,
      });
      const saved = await this.dbSaveService.saveChanges();
      if (!saved) return;
      this.toastr.success(`The system created the color '${name}'.`);
      await this.reloadDetail();
    } catch (e: any) {
      this.uow.rollback();
      await this.dialogService.showOkMessage(
        "Create Color",
        `The system could not create the color. ${e?.message ?? ""}`);
    }
  }

  /**
   * Open this color on References: Features with the Color feature and the color selected.
   *
   * Deliberately not an edit dialog. A color is shared across every style that uses it, so
   * renaming 'Black' to 'Black Ink' from here would silently rewrite products this page never
   * showed. The Features page lists those products next to the color, so the operator sees
   * the blast radius before they change anything.
   */
  async viewColorInReferences(row: UnmatchedUniformaxColorDTO) {
    await this.router.navigate(["/references/manage/features"], {
      queryParams: { key: await this.dbQueryService.getColorFeatureId(), fcid: row.FeatureChoiceId },
    });
  }

  /**
   * Re-read the style so the counts above and the color cells below agree again.
   *
   * Runs the name matcher first. Every caller of this has just changed the Uniformax side -
   * created a color, moved one - and the staged row holds the correspondence between the two
   * sides. Only the matcher writes that, so re-querying alone showed the same stale answer:
   * create the exact color the feed published and the cell went on offering Create, because
   * nothing had joined the two names up. Re-reading the style from the manufacturer was the
   * only cure, and for a bulk feed that means re-opening the whole weekly file.
   */
  private async reloadDetail() {
    if (this.detail == null) return;
    const id = this.detail.Style.Id;
    await this.api.reresolveStyle(id);
    await this.refresh();
    await this.loadDetail(id);
  }

  // ------------------------------------------------------------------

  onStyleGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: "Style", field: "Style", filter: "agTextColumnFilter", ...AgFns.flexWidth(120) },
      { headerName: "Product Name", field: "ProductName", filter: "agTextColumnFilter", ...AgFns.flexWidth(220) },
      { headerName: "Brand", field: "Brand", filter: "agTextColumnFilter", ...AgFns.flexWidth(120) },
      {
        headerName: "In Uniformax",
        field: "ProductTypeId",
        ...AgFns.fixWidth(140),
        type: "leftAligned",
        // Retired is its own answer. 'Yes' on a style somebody already took out of the catalogue
        // is what made 186000 look like outstanding work. A custom style is ours but does not use
        // the feed, so the feed's refusal of it is expected.
        valueGetter: (p: any) =>
          !p.data?.ProductTypeId ? "No"
            : p.data.IsProductTypeInactive ? "Retired"
            : p.data.IsCustomStyle ? "Custom style"
            : "Yes",
        cellStyle: (p: any) =>
          p.value === "Retired" || p.value === "Custom style"
            ? { color: "var(--p-text-muted-color, #6b7280)" } : null,
      },
      {
        // More than one Uniformax ProductType on a single manufacturer style is a duplicate.
        // The Uniformax counts to the right are aggregated across all of them.
        headerName: "Dup Styles",
        field: "UniformaxProductTypeCount",
        ...AgFns.fixWidth(125),
        type: "numericColumn",
        cellStyle: (p: any) =>
          p.value > 1 ? { color: "var(--p-red-500, #ef4444)", fontWeight: "600" } : null,
        valueGetter: (p: any) =>
          (p.data?.UniformaxProductTypeCount ?? 0) > 1 ? p.data.UniformaxProductTypeCount : null,
      },
      // The Manufacturer and Uniformax count groups used to sit here - staged colors and parts,
      // our colors, matched, not in feed. Five numeric columns that pushed the Error off the
      // right of the screen, which is the one thing anybody comes to this grid to read. The
      // counts are still on the style detail below and in the health panel; they do not need to
      // be in the worklist as well.
      {
        // The only signal that a style needs SIZE work. Colors have carried their count since
        // the beginning; sizes carried nothing, so a style with every size unanswered looked
        // identical to a finished one. One column, not the five that were just removed - this
        // is the worklist number, the rest live on the style detail and the health panel.
        headerName: "Sizes to Answer",
        field: "UnansweredSizeCount",
        ...AgFns.fixWidth(150),
        type: "numericColumn",
        cellStyle: (p: any) =>
          p.value > 0 ? { color: "var(--p-red-500, #ef4444)", fontWeight: "600" } : null,
        valueGetter: (p: any) =>
          (p.data?.UnansweredSizeCount ?? 0) > 0 ? p.data.UnansweredSizeCount : null,
      },
      {
        headerName: "Snapshot",
        field: "SnapshotTs",
        ...AgFns.fixWidth(150),
        valueFormatter: (p: any) => (p.value ? new Date(p.value).toLocaleString() : ""),
      },
      {
        headerName: "Needs Attention",
        field: "AttentionReasons",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(260),
        valueGetter: (p: any) => ((p.data as StagedProductTypeDTO)?.AttentionReasons ?? []).join(" "),
      },
      {
        headerName: "Error",
        field: "FetchError",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(200),
        // The blocker belongs beside the refusal that caused somebody to look here. Finding out
        // on the next screen that a style cannot be retired is a wasted trip.
        valueGetter: (p: any) => {
          const row = p.data as StagedProductTypeDTO;
          const blocker = row?.FetchError ? this.retireBlocker(row) : null;
          return blocker ? `${row.FetchError} ${blocker}` : row?.FetchError;
        },
      },
    ];
    // Was sorted on the unmatched-color count, which is no longer a column. Style order is what
    // a worklist gets read in anyway.
    AgFns.initGrid(event, colDefs, [{ colId: "Style", sort: "asc" }]);
  }

  onColorGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "",
        field: "ApproximatePms",
        ...AgFns.fixWidth(44),
        sortable: false,
        filter: false,
        // Only draw a chip when the Pantone code resolves to a real color. Most SanMar
        // values are bare numeric codes (533C, 7540C) that no keyword table can read, and a
        // placeholder grey chip is worse than an empty cell: two different navies would show
        // the same swatch on exactly the comparison the operator needs it for.
        cellRenderer: (p: any) => {
          const css = StagedFeedComponent.pmsToCss(p.value);
          if (css == null) return "";
          return `<span title="${p.value}" style="display:inline-block;width:14px;height:14px;`
            + `border-radius:3px;border:1px solid var(--p-surface-400,#9ca3af);background:${css}"></span>`;
        },
      },
      {
        headerName: "Standard Color Name",
        field: "StandardColorName",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(200),
      },
      { headerName: "Catalogue Name", field: "ColorName", filter: "agTextColumnFilter", ...AgFns.flexWidth(170) },
      { headerName: "Pantone", field: "ApproximatePms", ...AgFns.fixWidth(130), type: "leftAligned" },
      {
        headerName: "Uniformax Color",
        field: "UniformaxColorName",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(190),
        // Matched rows render the Uniformax name as plain text. Unmatched rows render a Create
        // button in its place - returning undefined falls back to the default text renderer.
        cellRendererSelector: (p: any) =>
          p.data?.FeatureChoiceId == null
            ? {
                component: ButtonRendererComponent,
                params: {
                  onClick: this.createColor.bind(this),
                  label: "Create",
                  tooltip: "Add this color to the Uniformax color list",
                },
              }
            : undefined,
      },
      { headerName: "Sizes", field: "Sizes", ...AgFns.flexWidth(200) },
      {
        // Our vocabulary beside theirs, the same pairing the color columns already use.
        // Unmatched sizes are called out rather than silently omitted, so a half-answered
        // style cannot read as a finished one.
        headerName: "Uniformax Sizes",
        field: "UniformaxSizes",
        ...AgFns.flexWidth(200),
        cellClass: (p: any) =>
          ((p.value ?? "").indexOf("not matched") >= 0 ? "rs-size-unmatched" : ""),
      },
      {
        headerName: "Closeout",
        field: "IsCloseout",
        ...AgFns.fixWidth(125),
        valueGetter: (p: any) => (p.data?.IsCloseout ? "Yes" : ""),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: "StandardColorName", sort: "asc" }]);
  }

  onUnmatchedGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createButtonProps("Change", this.repointColor.bind(this), { label: "Change" }),
        ...AgFns.fixWidth(115),
      },
      {
        headerName: "Uniformax Color",
        field: "UniformaxColorName",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(220),
        cellRenderer: ButtonRendererComponent,
        cellRendererParams: {
          onClick: this.viewColorInReferences.bind(this),
          calcLabel: (row: UnmatchedUniformaxColorDTO) => row?.UniformaxColorName ?? "",
          tooltip: "See which products use this color before you change it",
        },
      },
      { headerName: "Products", field: "ProductCount", ...AgFns.fixWidth(130), type: "numericColumn" },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: "UniformaxColorName", sort: "asc" }]);
  }

  /**
   * Rough Pantone-to-CSS so the swatch shows something rather than nothing. A real
   * Pantone table is a fixed offline lookup and belongs here later; this covers the
   * common families so the column is useful today.
   */
  /**
   * Resolve SanMar's `approximatePms` to a CSS color, or null when the code is unknown.
   *
   * Tries the Pantone table first, then the coated equivalent for uncoated (U) and color
   * bridge (CP/UP) variants - the same ink on a different substrate, and a far better answer
   * than nothing - then the bare number.
   *
   * Returns null rather than a placeholder when nothing matches. A default grey chip claimed
   * color information the data did not carry, and rendered two different navies identically
   * on exactly the comparison the chip exists to support.
   */
  static pmsToCss(pms: string): string | null {
    const key = (pms || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    if (!key) return null;

    const direct = PANTONE_HEX[key];
    if (direct) return direct;

    const coated = PANTONE_HEX[key.replace(/(u|cp|up)$/, "c")];
    if (coated) return coated;

    const m = key.match(/^(\d+)(c|u|cp|up)?$/);
    if (m) {
      const bare = String(Number(m[1]));
      return PANTONE_HEX[bare + "c"] ?? null;
    }
    return null;
  }
}
